import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../config/env';
import { getNextServer, markServerFailed, markServerSuccess } from './loadBalancer';

const USE_LOAD_BALANCER = process.env.REACT_APP_USE_LOAD_BALANCER === 'true';

const api = axios.create({
  baseURL: USE_LOAD_BALANCER ? undefined : config.api.baseURL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  timeout: 30000
});

const cache = new Map();
const pendingRequests = new Map();

const CACHE_TTL = {
  default: 5 * 60 * 1000,
  problems: 10 * 60 * 1000,
  problem: 30 * 60 * 1000,
  leaderboard: 10 * 60 * 1000,
  challenges: 15 * 60 * 1000,
  challenge: 30 * 60 * 1000,
  submissions: 2 * 60 * 1000,
  health: 30 * 1000
};

const getCacheKey = (config) => `${config.method}:${config.url}:${JSON.stringify(config.params || {})}`;

const getCacheTTL = (url) => {
  if (url.includes('/api/problems/') && !url.includes('/api/problems?')) return CACHE_TTL.problem;
  if (url.includes('/api/problems')) return CACHE_TTL.problems;
  if (url.includes('/api/leaderboard')) return CACHE_TTL.leaderboard;
  if (url.includes('/api/challenges/') && !url.includes('/api/challenges?')) return CACHE_TTL.challenge;
  if (url.includes('/api/challenges')) return CACHE_TTL.challenges;
  if (url.includes('/api/submissions')) return CACHE_TTL.submissions;
  if (url.includes('/api/health')) return CACHE_TTL.health;
  return CACHE_TTL.default;
};

api.interceptors.request.use(
  (config) => {
    if (USE_LOAD_BALANCER) {
      try {
        const server = getNextServer();
        config.baseURL = server;
        config.metadata = { server, startTime: Date.now() };
        
      } catch (error) {
        
        config.baseURL = 'https://codebattle-proxy.onrender.com';
      }
    } else {
      if (!window.__backendLogged) {
        
        window.__backendLogged = true;
      }
    }
    
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;

    if (config.method === 'get' && !config.skipCache) {
      const cacheKey = getCacheKey(config);
      config._cacheKey = cacheKey;
      
      const cached = cache.get(cacheKey);
      const ttl = getCacheTTL(config.url);
      
      if (cached && Date.now() - cached.timestamp < ttl) {
        config.adapter = () => Promise.resolve({
          data: cached.data,
          status: 200,
          statusText: 'OK (cached)',
          headers: {},
          config
        });
        return config;
      }
      
      if (pendingRequests.has(cacheKey)) {
        config.adapter = () => pendingRequests.get(cacheKey);
        return config;
      }
      
      config._isPending = true;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    if (USE_LOAD_BALANCER && response.config.metadata?.server) {
      const responseTime = Date.now() - (response.config.metadata.startTime || 0);
      markServerSuccess(response.config.metadata.server, responseTime);
    }
    
    if (response.config.method === 'get' && !response.config.skipCache && response.config._cacheKey) {
      const cacheKey = response.config._cacheKey;
      const ttl = getCacheTTL(response.config.url);
      
      cache.set(cacheKey, { data: response.data, timestamp: Date.now(), ttl });
      
      if (response.config._isPending) {
        pendingRequests.set(cacheKey, Promise.resolve(response));
        setTimeout(() => pendingRequests.delete(cacheKey), 100);
      } else {
        pendingRequests.delete(cacheKey);
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    if (originalRequest?.method === 'get' && !originalRequest?.skipCache && originalRequest?._cacheKey) {
      pendingRequests.delete(originalRequest._cacheKey);
    }
    
    if (USE_LOAD_BALANCER && originalRequest?.metadata?.server) {
      markServerFailed(originalRequest.metadata.server);
      
      if (!originalRequest._retry && (!originalRequest._retryCount || originalRequest._retryCount < 2)) {
        originalRequest._retry = true;
        originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
        
        await new Promise(resolve => setTimeout(resolve, 500));
        return api(originalRequest);
      }
    }
    
    if (error.name === 'CanceledError' || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }
    
    const reqUrl = originalRequest?.url ?? '';
    const fullUrl = [originalRequest?.baseURL, reqUrl].filter(Boolean).join('');
    const isSilentRequest = [reqUrl, fullUrl].some(u => 
      String(u).includes('/api/auth/me') || String(u).includes('/api/status')
    );

    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      if (!originalRequest._timeoutRetry) {
        originalRequest._timeoutRetry = true;
        originalRequest.timeout = 60000;
        await new Promise(resolve => setTimeout(resolve, 1000));
        return api(originalRequest);
      }
      if (!isSilentRequest) {
        toast.error('Request timeout. Please check your connection.', { className: 'text-sm' });
      }
      return Promise.reject(error);
    }

    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      if (!originalRequest._networkRetry && (!originalRequest._retryCount || originalRequest._retryCount < 2)) {
        originalRequest._networkRetry = true;
        originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
        await new Promise(resolve => setTimeout(resolve, 2000));
        return api(originalRequest);
      }
      if (!isSilentRequest) {
        toast.error('Network error. Please check your connection.', { className: 'text-sm' });
      }
      return Promise.reject(error);
    }
    
    const { status, config } = error.response || {};
    const isChallengeJoinRequest = config?.url?.includes('/api/challenges/') && config?.url?.includes('/join');
    
    if (status === 401) {
      if (isChallengeJoinRequest) {
        toast.error(error.response?.data?.message || 'Incorrect password.', { className: 'text-sm' });
      }
      // Do NOT clear token here - only AuthContext clears token when /api/auth/me returns 401.
      // This prevents a stray 401 from another endpoint (e.g. cached, race) from logging the user out on refresh.
    } else if (status === 403) {
      if (error.response?.data?.requiresJoin && error.response?.data?.challengeId) {
        const challengeId = error.response.data.challengeId;
        const currentPath = window.location.pathname;
        
        if (currentPath.includes('/problem/')) {
          return Promise.reject(error);
        }
        
        if (!currentPath.includes(`/challenges/${challengeId}`)) {
          toast.error('You must join this challenge first', { position: 'top-center', duration: 3000 });
          setTimeout(() => window.location.href = `/challenges/${challengeId}`, 800);
        }
      }
    } else if (status === 404) {
      if (!config?.url?.includes('/api/auth/me')) {
        toast.error('Resource not found.', { className: 'text-sm' });
      }
    } else if (status >= 500) {
      toast.error('Server error. Please try again later.', { className: 'text-sm' });
    } else if (error.response?.data?.message || error.response?.data?.error) {
      const errorMessage = error.response.data.message || error.response.data.error;
      if (!config?.url?.includes('/api/auth/me')) {
        toast.error(errorMessage, { className: 'text-sm' });
      }
    }
    
    return Promise.reject(error);
  }
);

export async function getFriendInviteLink() {
  const res = await api.post('/api/friends/invite-link');
  return res.data.inviteLink;
}

export const prefetch = async (url, params = {}) => {
  const cacheKey = `get:${url}:${JSON.stringify(params)}`;
  const cached = cache.get(cacheKey);
  const ttl = getCacheTTL(url);
  
  if (cached && Date.now() - cached.timestamp < ttl) return cached.data;
  if (pendingRequests.has(cacheKey)) return pendingRequests.get(cacheKey);
  
  const promise = api.get(url, { params, skipCache: false }).then(res => res.data);
  pendingRequests.set(cacheKey, promise);
  promise.finally(() => pendingRequests.delete(cacheKey));
  return promise;
};

export const clearCache = (pattern = null) => {
  if (!pattern) {
    cache.clear();
    pendingRequests.clear();
    return;
  }
  
  for (const [key] of cache) {
    if (key.includes(pattern)) cache.delete(key);
  }
};

export default api;